import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command"
import {
  Calendar,
  BarChart3,
  Users,
  FileText,
  Settings,
  Home,
  Building2,
  UserPlus,
  ShoppingCart,
  Truck,
  Files,
  
  DollarSign,
  Receipt,
  Wallet,
  Box,
  UserCheck,
  Plus,
  CheckSquare,
  Sparkles,
} from "lucide-react"
import { useI18n } from "@/hooks/useI18n"
import { useAIChat } from "@/contexts/AIChatContext"

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const { openChat } = useAIChat()

  const handleSelect = (callback: () => void) => {
    onOpenChange(false)
    callback()
  }

  // Comandos de navegação
  const navigationCommands = [
    {
      group: t("sidebar.event_management"),
      items: [
        { 
          icon: BarChart3, 
          label: t("navigation.dashboard_events"), 
          action: () => navigate("/dashboard-eventos"),
          keywords: "dashboard eventos analytics"
        },
        { 
          icon: Calendar, 
          label: t("navigation.events"), 
          action: () => navigate("/eventos"),
          keywords: "eventos casamento"
        },
        { 
          icon: Truck, 
          label: t("navigation.suppliers"), 
          action: () => navigate("/fornecedores-geral"),
          keywords: "fornecedores vendedores"
        },
        { 
          icon: Files, 
          label: t("navigation.models"), 
          action: () => navigate("/modelos"),
          keywords: "modelos templates"
        },
      ]
    },
    {
      group: t("sidebar.company_management"),
      items: [
        { 
          icon: Building2, 
          label: t("navigation.dashboard_company"), 
          action: () => navigate("/dashboard-geral"),
          keywords: "dashboard empresa company"
        },
        { 
          icon: UserPlus, 
          label: t("navigation.crm"), 
          action: () => navigate("/crm"),
          keywords: "crm leads clientes potenciais"
        },
        { 
          icon: ShoppingCart, 
          label: t("navigation.sales"), 
          action: () => navigate("/vendas"),
          keywords: "vendas sales"
        },
        { 
          icon: UserCheck, 
          label: t("navigation.clients"), 
          action: () => navigate("/clientes"),
          keywords: "clientes customers"
        },
        { 
          icon: FileText, 
          label: t("navigation.contracts"), 
          action: () => navigate("/contratos"),
          keywords: "contratos contracts"
        },
        { 
          icon: DollarSign, 
          label: t("navigation.budget"), 
          action: () => navigate("/orcamento-empresa"),
          keywords: "orcamento budget"
        },
        { 
          icon: Receipt, 
          label: t("navigation.accounts_payable"), 
          action: () => navigate("/contas-pagar"),
          keywords: "contas pagar"
        },
        { 
          icon: Wallet, 
          label: t("navigation.accounts_receivable"), 
          action: () => navigate("/contas-receber"),
          keywords: "meus recebimentos contas receber recebiveis"
        },
        { 
          icon: Box, 
          label: t("navigation.patrimony"), 
          action: () => navigate("/patrimonio"),
          keywords: "patrimonio assets"
        },
      ]
    },
    {
      group: t("sidebar.configurations"),
      items: [
        { 
          icon: Settings, 
          label: "Geral", 
          action: () => navigate("/configuracoes"),
          keywords: "configuracoes settings"
        },
      ]
    }
  ]

  // Ações rápidas
  const quickActions = [
    {
      icon: Plus,
      label: "Novo Evento",
      action: () => navigate("/eventos?new=true"),
      keywords: "novo criar evento new"
    },
    {
      icon: UserPlus,
      label: "Novo Cliente",
      action: () => navigate("/clientes?new=true"),
      keywords: "novo criar cliente customer new"
    },
    {
      icon: CheckSquare,
      label: "Nova Tarefa",
      action: () => navigate("/tarefas-hoje"),
      keywords: "nova criar tarefa task new"
    },
  ]

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Digite um comando ou busque..." />
      <CommandList>
        <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
        
        <CommandGroup heading="Ajuda">
          <CommandItem
            onSelect={() => handleSelect(() => openChat())}
            keywords={["ajuda", "assistente", "ia", "suporte", "chat"]}
          >
            <Sparkles className="mr-2 h-4 w-4 text-purple-500" />
            <span>Falar com Assistente IA</span>
            <CommandShortcut>Shift + ?</CommandShortcut>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Ações Rápidas">
          {quickActions.map((action, index) => (
            <CommandItem
              key={index}
              onSelect={() => handleSelect(action.action)}
              keywords={[action.keywords]}
            >
              <action.icon className="mr-2 h-4 w-4" />
              <span>{action.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {navigationCommands.map((section, sectionIndex) => (
          <CommandGroup key={sectionIndex} heading={section.group}>
            {section.items.map((item, itemIndex) => (
              <CommandItem
                key={itemIndex}
                onSelect={() => handleSelect(item.action)}
                keywords={[item.keywords]}
              >
                <item.icon className="mr-2 h-4 w-4" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  )
}
